import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./ContentHub.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
// TODO: For further performance, consider splitting ContentHub.css into smaller CSS modules or using CSS-in-JS/code-splitting for above-the-fold and below-the-fold styles.

// Memoized BlogCard to avoid unnecessary re-renders
const BlogCard = React.memo(function BlogCard({ post }: { post: BlogPost }) {
  return (
    <div className="blog-card" key={post.id}>
      {post.image_url && (
        <img
          src={post.image_url}
          alt={post.title}
          className="blog-card-image"
        />
      )}
      <div className="blog-card-content">
        <Link
          to={`/resources/content-hub/${post.slug}/`}
          state={{ post }}
          className="blog-title"
          onClick={() => window.scrollTo(0, 0)}
        >
          {post.title}
        </Link>
        <div className="blog-meta-row">
          <div className="blog-date">
            {new Date(post.created_at).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          {post.topic_display && (
            <div className="blog-topic-label">{post.topic_display}</div>
          )}
        </div>
        <p
          className="blog-snippet"
          dangerouslySetInnerHTML={{
            __html: post.content.replace(/<[^>]+>/g, "").slice(0, 150) + "...",
          }}
        />
        <Link
          to={`/resources/content-hub/${post.slug}/`}
          state={{ post }}
          className="blog-read-more"
          onClick={() => window.scrollTo(0, 0)}
        >
          Learn more →
        </Link>
      </div>
    </div>
  );
});

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  author_name: string;
  status: "draft" | "published" | "archived";
  created_at: string;
  updated_at: string;
  source: string;
  image_url: string;
  topic?: string;
  topic_display?: string;
}

const apiUrl =
  window._env_?.VITE_API_BASE_URL !== "${VITE_API_BASE_URL}"
    ? window._env_.VITE_API_BASE_URL
    : "/api";

const token = window._env_?.BASE_TOKEN;

const ContentHub: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [totalPosts, setTotalPosts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${apiUrl}/external/api/blogs/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            status: "published",
            source: "apollo",
            page,
            page_size: pageSize,
            ...(debouncedSearch && { search: debouncedSearch }),
            ...(selectedTopic && { topic: selectedTopic }),
          },
        });
        setPosts(res.data.results);
        setTotalPosts(res.data.count);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / pageSize)));
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, [page, pageSize, debouncedSearch, selectedTopic]);

  // Memoize blog cards rendering (must be after hooks, before return)
  const blogCards = React.useMemo(
    () => posts.map((post) => <BlogCard key={post.id} post={post} />),
    [posts]
  );

  return (
    <div className="content-hub-container">
      <Helmet>
        <title>Apollo Content Hub | Financial Insights & News</title>
        <meta
          name="description"
          content="Explore financial perspectives, investment trends, and the latest thought leadership from Apollo Financial Services."
        />
        <meta property="og:title" content="Apollo Financial Content Hub" />
        <meta
          property="og:description"
          content="Explore financial perspectives and insights from Apollo Financial."
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Apollo Financial Content Hub",
            description:
              "Browse financial articles, insights, and thought leadership from Apollo Financial Services.",
            url: "https://www.apollofinancial.ie/resources/content-hub/",
            publisher: {
              "@type": "Organization",
              name: "Apollo Financial Services",
              logo: {
                "@type": "ImageObject",
                url: "https://www.apollofinancial.ie/assets/logo2.png",
              },
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.apollofinancial.ie/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Content Hub",
                item: "https://www.apollofinancial.ie/resources/content-hub/",
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="content-hub-hero">
        <div className="content-hub-hero-content">
          <h1>Content hub</h1>
          <p>
            The Content Hub brings together expert articles, updates, and advice
            to support every step of your financial journey.
          </p>
        </div>
      </div>
      <div className="content-hub-filters">
        <input
          type="text"
          placeholder="Search articles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="content-hub-search-input"
        />
        <select
          value={selectedTopic}
          onChange={(e) => {
            setSelectedTopic(e.target.value);
            setPage(1);
          }}
          className="content-hub-topic-dropdown"
        >
          <option value="">All Topics</option>
          <option value="pension">Pension</option>
          <option value="retirement">Retirement</option>
          <option value="life_protection">Life & Protection</option>
          <option value="investments">Investments</option>
        </select>
      </div>

      <div className="blog-tiles">
        {isLoading ? (
          <p>Loading...</p>
        ) : posts.length === 0 ? (
          <p>No content uploaded yet. Please check back soon.</p>
        ) : (
          blogCards
        )}
      </div>

      <div className="pagination">
        <button
          onClick={() => {
            page > 1 && setPage(page - 1);
            window.scrollTo(0, 0);
          }}
          disabled={page <= 1}
          className="pagination-btn"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span style={{ marginLeft: "8px" }}>Prev</span>
        </button>
        <span>{`Page ${page} of ${totalPages}`}</span>
        <button
          onClick={() => {
            page < totalPages && setPage(page + 1);
            window.scrollTo(0, 0);
          }}
          disabled={page >= totalPages}
          className="pagination-btn"
        >
          <span style={{ marginRight: "8px" }}>Next</span>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
      <div className="page-size-container">
        <div className="page-size-selector">
          <label>Page Size:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setPage(1);
              window.scrollTo(0, 0);
            }}
            className="page-size-dropdown"
          >
            {[6, 12, 24].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="page-size-selector">
          <p>
            {(page - 1) * pageSize + 1} -{" "}
            {Math.min(page * pageSize, totalPosts)} of {totalPosts} Articles
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContentHub;
